import {
  applyOptionsVersion, drawnScenarioId, markScenarioReviewed, remapVersionsToScenarios,
  scenarioInForce, syncScenarioBranches, type ScenarioLeaderVersion, type ScenarioRowTemplate, unreviewedCount,
  type DisplayVersion, type ScenarioBranchBase,
} from '@agavi/blocks';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ScenarioLinkMenu } from './scenario-link-menu';
import { ScenarioRemapModal } from './scenario-remap-modal';
import { ScenarioStimulus } from './scenario-stimulus';
import { useScenarioRowProjection } from './use-scenario-rows';
import { useVersionInForce } from './version-in-force';
import { VersionSelector } from './version-selector';

import type { Block } from '@/types/experience';

/**
 * Everything an INPUT block needs to follow a scenario, so no block has to remember it.
 *
 * Dropdown and MCQ were ported by hand and each took about thirteen separate edits in the same
 * thirteen places. Eight blocks remained. That is the arithmetic `use-block-versions` already
 * records for the question banks — the subtle parts are subtle in the same way every time, and
 * the copies drift — so the second port became the template and this is the third onwards.
 *
 * ## What is genuinely per block, and therefore a parameter
 *
 * Only the PAYLOAD. A row carries whatever that block's conditional branch carries: roles for
 * the five choice blocks, an order for Ordering, a target for GPS, value fields for the
 * numeric ones. `rowTemplate` says what a new row holds and `rowFromVersion` says how an old
 * version becomes one. Everything else below is the same question with the same answer.
 *
 * ## What is shared, and why each piece is not obvious
 *
 * - **The pointer is the LEADER's, not this block's.** At runtime a whole group resolves from
 *   one key (§3.1), and the editor has no business using a different rule. Read through
 *   `useVersionInForce` on the leader, which is the pointer that leader's own card is already
 *   paging — so the leader's prose, this block's live test and the row being authored become
 *   one fact that cannot disagree with itself.
 * - **Linking and unlinking are destructive in opposite directions.** Linking clears the
 *   equation and its branches; unlinking drops the rows. Without `linkSwapRef` each
 *   throws the other's work away for good, and a teacher who unlinked to look at something
 *   gets every scenario's answer back at its copied default.
 * - **The bank collapses to VERSION 1 on linking, not to the block's own copy.** The top-level
 *   copy is the vestigial one `writeOptionsVersion` freezes when the first version is made —
 *   usually version 1's text, but only by accident of how the bank was built. Saying "version
 *   1" and meaning it is what lets the remap modal state the consequence plainly.
 * - **A new row is seeded from the EDITOR's feedback, not from stored data.** A block whose
 *   feedback has never been written back still holds `{ states: [] }` in storage, because the
 *   defaults are produced on mount and only reach storage on the next save. Link a brand-new
 *   block before touching anything and every scenario is born unanswerable.
 */
export function useScenarioFollower<Row extends ScenarioBranchBase>({
  block, blocks, gradingMode, feedback, isEditing, isPreview,
  storedData, restoredRows, initialRows,
  emit, emitFields, applyVersion,
  rowTemplate, rowFromVersion,
  numeric,
}: {
  block: Block;
  blocks: Block[] | undefined;
  gradingMode: string;
  /** The EDITOR's normalized feedback — see the note above about `{ states: [] }`. */
  feedback: unknown;
  isEditing: boolean;
  /**
   * PREVIEW GETS NO STEM. Owned here rather than left to each call site, because the answer
   * displays are SHARED between the canvas test card, the phone test pane and Preview — so
   * "remember not to pass it in Preview" is a rule eleven call sites would have to keep and
   * seven of them had already dropped.
   *
   * The reason is the frame's own: in Preview the leader block is itself on the page rendering
   * its drawn variant, so a copy inside the follower prints the stem twice. The ghosted copy
   * is a teacher's testing aid, never part of what a student is shown.
   */
  isPreview?: boolean;
  /** `initData(block.data)`, which must carry `followsBlockId` and `scenarioBranches`. */
  storedData: { versions?: { id: string }[]; followsBlockId?: string; scenarioBranches?: Row[] } & Record<string, unknown>;
  /** The rows `useRestoredData` is showing, for the instance that is not editing. */
  restoredRows: Row[] | undefined;
  initialRows: Row[] | undefined;
  emit: (data: Record<string, unknown>) => void;
  emitFields: (next: Record<string, unknown>) => void;
  /** The block's own `apply…Version`, passed straight through — see `ScenarioLinkMenu`. */
  applyVersion: (data: never, versionId: string | undefined) => unknown;
  rowTemplate: () => ScenarioRowTemplate<Row>;
  /** How one of this block's own versions becomes a row — the §7.1 remap. */
  rowFromVersion: (version: { id: string } & Record<string, unknown>) => ScenarioRowTemplate<Row>;
  /**
   * The numeric branch state this block holds, so linking can put it aside and unlinking can
   * hand it back. Read and written by the block, because it lives in the block's own state.
   */
  numeric: {
    read: () => { equation: string | undefined; branches: unknown[] | undefined };
    write: (equation: string | undefined, branches: unknown[] | undefined) => void;
  };
}) {
  const { t } = useTranslation();
  const [editedRows, setRows] = useState<Row[] | undefined>(initialRows);
  const rows = isEditing ? editedRows : restoredRows;

  const linkSwapRef = useRef<{
    scenario?: { leaderId: string; rows: Row[] | undefined };
    numeric?: { equation: string | undefined; branches: unknown[] | undefined };
  }>({});
  const [pendingLink, setPendingLink] = useState<string | null>(null);

  /** Every write to the rows, state and storage together — the editor renders from state and
   *  the card grades off storage, so a handler that moved one would show a change that did not
   *  take. */
  function pushScenarioRows(next: Row[] | undefined) {
    setRows(next);
    emitFields({ scenarioBranches: next });
  }

  function linkToLeader(followsBlockId: string | undefined, remap?: 'remap' | 'empty') {
    const current = storedData;
    // A bank is the one case that needs a decision before anything is written — §7.1.
    if (followsBlockId && !remap && (current.versions?.length ?? 0) > 1) {
      setPendingLink(followsBlockId);
      return;
    }
    if (!followsBlockId) {
      if (current.followsBlockId) {
        linkSwapRef.current.scenario = { leaderId: current.followsBlockId, rows: current.scenarioBranches };
      }
      const back = linkSwapRef.current.numeric;
      linkSwapRef.current.numeric = undefined;
      setRows(undefined);
      numeric.write(back?.equation, back?.branches);
      emit({
        ...current,
        followsBlockId: undefined,
        scenarioBranches: undefined,
        conditionalEquation: back?.equation,
        conditionalBranches: back?.branches,
      });
      return;
    }
    const leaderVersionsNow = ((blocks ?? []).find(b => b.id === followsBlockId)?.data as
      { versions?: ScenarioLeaderVersion[] } | undefined)?.versions;
    const base = (current.versions?.length ?? 0) > 1
      ? applyOptionsVersion(current as never, current.versions![0]!.id) as typeof current
      : current;
    const held = numeric.read();
    if (held.equation !== undefined || held.branches !== undefined) linkSwapRef.current.numeric = held;
    const banked = linkSwapRef.current.scenario;

    const { branches } = remap === 'remap'
      ? remapVersionsToScenarios<{ id: string } & Record<string, unknown>, Row>(
        leaderVersionsNow, current.versions as ({ id: string } & Record<string, unknown>)[] | undefined,
        rowFromVersion, rowTemplate,
      )
      : syncScenarioBranches<Row>(
        leaderVersionsNow,
        current.scenarioBranches ?? (banked?.leaderId === followsBlockId ? banked.rows : undefined),
        rowTemplate,
      );
    /* A row with no feedback gets the block's, whatever produced it. The template only runs for
       rows being CREATED; a row banked by an unlink, or written by an earlier build, arrives
       empty, and healing at the write is what makes relinking a repair. */
    const healed = branches.map(b => {
      const f = (b as { feedback?: { states?: unknown[] } }).feedback;
      return f?.states?.length ? b : { ...b, feedback };
    }) as Row[];

    setRows(healed);
    numeric.write(undefined, undefined);
    emit({
      ...base,
      feedback: remap === 'remap' ? ((base.feedback as unknown) ?? feedback) : feedback,
      followsBlockId,
      scenarioBranches: healed,
      // The bank cannot survive the link (§2.2) — remapped into the rows, or discarded on the
      // teacher's say-so. Either way the decision was made above, never here.
      versions: undefined,
      conditionalEquation: undefined,
      conditionalBranches: undefined,
    });
  }

  /** §4.1's projection, kept live — a variant added to the leader becomes a row here. */
  useScenarioRowProjection<Row>({
    followsBlockId: storedData.followsBlockId,
    blocks,
    rows,
    template: rowTemplate,
    onSync: pushScenarioRows,
  });

  /** The leader itself, not just its variants — the ghosted stem renders by its TYPE. */
  const leaderBlock = (blocks ?? []).find(b => b.id === storedData.followsBlockId);
  const leaderVersions = ((blocks ?? []).find(b => b.id === storedData.followsBlockId)?.data as
    { versions?: DisplayVersion[] } | undefined)?.versions;
  const [leaderShownRaw, setLeaderShown] = useVersionInForce(storedData.followsBlockId ?? block.id, 'test');
  const shownScenarioId = leaderShownRaw ?? leaderVersions?.[0]?.id;

  /** Gated on gradingMode for the reason `activeBranch` is: an answer key is a graded-mode fact,
   *  and a block switched to Participation has nothing for these rows to say. */
  const activeScenario = gradingMode === 'graded' && rows && rows.length > 0
    ? (rows.find(b => b.scenarioId === shownScenarioId) ?? rows[0])
    : undefined;
  const activeScenarioVersion = activeScenario
    ? scenarioInForce(leaderVersions, activeScenario.scenarioId) : null;
  /** For the TEST surface's ghosted stem — NOT gated on graded mode, because a participation
   *  block still asks about the scenario and a teacher testing one still needs to read it. */
  const shownScenarioVersion = storedData.followsBlockId
    ? scenarioInForce(leaderVersions, shownScenarioId) : null;

  /** What the test bar's pager pages: this block's own bank, or the leader's scenarios. */
  const testPager = storedData.followsBlockId
    ? { versions: (leaderVersions ?? []) as { id: string }[], activeId: shownScenarioId,
      onSelect: setLeaderShown, noun: 'scenario' as const }
    : null;

  const remapModal = pendingLink ? (
    <ScenarioRemapModal
      versionCount={storedData.versions?.length ?? 0}
      variantCount={((blocks ?? []).find(b => b.id === pendingLink)?.data as
        { versions?: unknown[] } | undefined)?.versions?.length ?? 0}
      leaderName={(blocks ?? []).find(b => b.id === pendingLink)?.name ?? ''}
      onCancel={() => setPendingLink(null)}
      onRemap={() => { const id = pendingLink; setPendingLink(null); linkToLeader(id, 'remap'); }}
      onLinkEmpty={() => { const id = pendingLink; setPendingLink(null); linkToLeader(id, 'empty'); }}
    />
  ) : null;

  /*
   * ONE MOUNT FOR EVERY BLOCK THAT FOLLOWS, which is why the driven half is wired here rather
   * than at each call site. Numerical, MCQ and Dropdown build their own copy of this menu and
   * are wired separately; everything else gets it from this line.
   */
  const linkMenu = (
    <ScenarioLinkMenu
      block={block}
      blocks={blocks}
      value={storedData.followsBlockId}
      onChange={linkToLeader}
      emit={emit}
      applyVersion={applyVersion}
    />
  );

  /** ONE pager, rendered in the answer-setup box and the feedback panel. Browse mode is also
   *  §4.1 in the UI: a follower's rows are the leader's to add and remove. */
  const pager = activeScenario ? (
    <VersionSelector
      versions={leaderVersions ?? []}
      activeId={activeScenario.scenarioId}
      onSelect={setLeaderShown}
      noun='scenario'
    />
  ) : null;

  /** The ghosted stem for a test surface — see `AnswerDisplayFrame`'s own note. */
  const ghostedScenario = isPreview ? null : (
    <ScenarioStimulus leader={leaderBlock} version={shownScenarioVersion as Record<string, unknown> | null} ghosted />
  );

  /** The read-only prose above the question, in the editor. */
  const editorScenario = (
    <ScenarioStimulus leader={leaderBlock} version={activeScenarioVersion as Record<string, unknown> | null} />
  );

  /** The feedback panel's own bar, naming the scenario instead of a comparison. */
  const feedbackScroller = activeScenario ? (
    <div className='flex items-center justify-center gap-2 border-b border-ws-border bg-ws-surface px-4 py-2'>
      <span className='truncate text-xs font-medium text-ws-muted'>
        {(activeScenarioVersion as { name?: string } | null)?.name}
      </span>
      {pager}
    </div>
  ) : null;

  /** Deciding a row IS reviewing it — §4.4's flag means "copied, not decided". */
  const writeActiveRow = (patch: Partial<Row>) => {
    if (!activeScenario) return;
    pushScenarioRows(markScenarioReviewed(
      (rows ?? []).map(b => (b.scenarioId === activeScenario.scenarioId ? { ...b, ...patch } : b)),
      activeScenario.scenarioId,
    ));
  };

  const dismissUnreviewed = () => {
    if (activeScenario) pushScenarioRows(markScenarioReviewed(rows ?? [], activeScenario.scenarioId));
  };

  return {
    rows, setRows, pushScenarioRows, writeActiveRow,
    following: !!storedData.followsBlockId,
    activeScenario, activeScenarioVersion, shownScenarioVersion, shownScenarioId,
    leaderVersions, testPager, pager, remapModal, linkMenu,
    ghostedScenario, editorScenario, feedbackScroller,
    unreviewedCount: unreviewedCount(rows),
    dismissUnreviewed,
    /** The scenario a STUDENT drew, for Preview. */
    drawnScenario: (roll: Record<string, string> | null | undefined) =>
      drawnScenarioId(storedData.followsBlockId, roll),
    /** The row's own issue, so a teacher who never pages to scenario 5 still learns of it. */
    unreviewedIssueDetail: (count: number) => t('blocks.scenario.unreviewedScenarios', { count }),
  };
}
